"use client";

import { BlockNoteView } from "@blocknote/mantine";
import { DefaultReactGridSuggestionItem, DefaultReactSuggestionItem, GridSuggestionMenuController, GridSuggestionMenuProps, SuggestionMenuController, useCreateBlockNote } from "@blocknote/react";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";

import "@blocknote/mantine/style.css";
import "./common.css";

import { BlockNoteSchema, defaultInlineContentSpecs, filterSuggestionItems, PartialBlock } from "@blocknote/core";
import Swal from "sweetalert2";

import {
  fetchWikiData,
  isDocumentEmpty,
  frontEndPoint,
  getArtifactDetails,
  getInitiativeDetails,
  getRandomColorFromSet,
  handleRedirections,
  saveToStorage,
  setSubscriptionAndInitiativeId,
  uploadFile
} from "./common";

import { Mention } from "./Mention";
import { ExtractedDocument } from "./ExtractedDocument";
import { pasteDocumentContent } from "./parseExtractedMarkdown";

// 🔐 Extract encoded data
const search = window.location.search;
const urlParams = new URLSearchParams(search);
const encodedData = urlParams.get("data");

type DocTheme = "light" | "dark";

let jsonData = {
  sourceAssetId: 0,
  alias: "",
  initiativeId: "",
  id: 0,
  userName: "",
  subscriptionId: "",
  teamId: 0,
  isAssociatedWithWorkItem: false,
  environmentName: "dev",
  userId: 0,
  // Platform theme, sent by covation in the base64 `data` payload. Live
  // changes arrive later via postMessage (see the message listener below).
  theme: "light" as DocTheme,
};

if (encodedData) {
  const decodedData = atob(encodedData);
  const {
    sourceAssetId = 0,
    alias,
    initiativeId,
    id,
    subscriptionId,
    userName,
    teamId = 0,
    isAssociatedWithWorkItem = false,
    environmentName,
    userId = 0,
    theme = "light",
  } = JSON.parse(decodedData);

  if (!alias || !initiativeId || !id || !subscriptionId || !userName) {
    handleRedirections();
  }

  jsonData = {
    sourceAssetId,
    alias,
    initiativeId,
    id,
    subscriptionId,
    userName,
    teamId,
    isAssociatedWithWorkItem,
    environmentName,
    userId,
    theme: theme === "dark" ? "dark" : "light",
  };
} else {
  handleRedirections();
}

// Paint the document chrome (body background, scrollbars, the "Loading…"
// state) to the platform theme BEFORE React mounts, so the iframe never
// flashes white on a dark host. common.css keys off this attribute.
const applyDocTheme = (theme: DocTheme) => {
  document.documentElement.setAttribute("data-doc-theme", theme);
};
applyDocTheme(jsonData.theme);

if (!jsonData.subscriptionId || !jsonData.initiativeId) {
  //@ts-ignore
  Swal.fire({
    icon: "warning",
    title: "Not Logged In",
    text: "You are not logged in. Please log in to continue.",
    showConfirmButton: false,
    timer: null,
    backdrop: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showClass: { popup: "" },
    hideClass: { popup: "" },
  });

  throw new Error("You are not logged in. Please log in to continue.");
}

// 🧠 Set default state
setSubscriptionAndInitiativeId(jsonData);

// 📜 Define schema with mention
const schema = BlockNoteSchema.create({
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    mention: Mention,
  },
});

function CustomEmojiPicker(
  props: GridSuggestionMenuProps<DefaultReactGridSuggestionItem>,
) {
  return (
    <div
      className={"emoji-picker"}
      style={
        { gridTemplateColumns: `repeat(${props.columns || 1}, 1fr)` } as any
      }
    >
      {props.items.map((item, index) => (
        <div
          className={`emoji-picker-item ${props.selectedIndex === index ? "selected" : ""
            }`}
          onClick={() => {
            props.onItemClick?.(item);
          }}
        >
          {item.icon}
        </div>
      ))}
    </div>
  );
}

// 🌟 Top-level component with YDocProvider
export default function App() {
  useEffect(() => {
    if (window.parent === window || !document.referrer) return;
    const root = document.getElementById('root');
    if (!root) return;
    const origin = new URL(document.referrer).origin;
    let previousHeight = 0;
    const reportHeight = () => {
      const height = Math.ceil(root.getBoundingClientRect().height) + 24;
      if (height !== previousHeight) {
        previousHeight = height;
        window.parent.postMessage({ type: 'DOCUMENT_HEIGHT', height }, origin);
      }
    };
    const observer = new ResizeObserver(reportHeight);
    observer.observe(root);
    reportHeight();
    return () => observer.disconnect();
  }, []);
  if (jsonData.sourceAssetId) return <ExtractedDocument assetId={jsonData.sourceAssetId} theme={jsonData.theme} />;
  return (
    <Document />
  );
}

// 📄 Main editor document component
let initiativeDetails: any = [];
let artifactDetails: any = [];
const docId = btoa(`${jsonData.subscriptionId}_${jsonData.initiativeId}_${jsonData.id}`);

function Document() {
  const [doc] = useState(() => new Y.Doc());
  const [provider] = useState<any>(() => new HocuspocusProvider({
    connect: false,
    url: "wss://live.shorterloop.com/",
    name: docId,
    document: doc,
    parameters: { userId: jsonData?.userId },
    onClose: ({ event }) => {
      if (event?.reason === 'Forbidden') {
        //@ts-ignore
        return Swal.fire({
          icon: "",
          title: "Error",
          text: "It looks like you're already editing this document in another tab. Please continue your work there to avoid conflicts.",
          showConfirmButton: false,
          timer: null,
          backdrop: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          showClass: { popup: '' }, // Disables animation
          hideClass: { popup: '' }  // Disables animation
        });
      }
    },
  }));

  useEffect(() => {
    provider.connect();
    return () => provider.disconnect();
  }, [provider]);

  const [loading, setLoading] = useState(true);

  // Editor theme. Seeded from the payload; kept live via the message bridge
  // below so toggling the platform theme re-themes an open document.
  const [theme, setTheme] = useState<DocTheme>(jsonData.theme);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // covation posts { type: 'theme', theme } to this iframe on every
      // platform toggle (page-content.component.ts#postThemeToIframe).
      const next = event?.data?.type === "theme" ? event.data.theme : null;
      if (next === "light" || next === "dark") {
        setTheme(next);
        applyDocTheme(next);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const [blocks, setBlocks] = useState<PartialBlock[]>();

  const fragment = doc.getXmlFragment("document-store");
  const editor = useCreateBlockNote({
    schema,
    pasteHandler: pasteDocumentContent,
    initialContent: blocks,
    collaboration: {
      // The Yjs Provider responsible for transporting updates:
      provider,
      // Where to store BlockNote data in the Y.Doc:
      fragment,
      // Information (name and color) for this user:
      user: {
        name: jsonData?.userName,
        color: getRandomColorFromSet(),
      },
    },
    uploadFile,
    trailingBlock: false,
  });


  const [initialized, setInitialized] = useState(false);
  const initializing = useRef(false);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    const init = async () => {
      if (initialized || initializing.current || !provider.synced || !editor) return;
      initializing.current = true;
      try {
        // An empty collaborative paragraph still has XML nodes. Test the
        // actual content, not whether the fragment has any structural nodes.
        if (isDocumentEmpty(editor)) await fetchWikiData(jsonData, editor);
        setInitialized(true);
        setLoading(false);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'The document could not be loaded.');
        return;
      } finally {
        initializing.current = false;
      }

      initiativeDetails = await getInitiativeDetails();
      artifactDetails = await getArtifactDetails();
    };

    // Attempt initialization as soon as provider is synced
    if (provider.synced && !initialized) {
      init();
    }

    // Listen for sync event for delayed initialization
    const handleSync = () => {
      if (!initialized) {
        init();
      }
    };

    provider.on("sync", handleSync);

    return () => {
      provider.off("sync", handleSync);
    };
  }, [provider, editor, initialized, loadAttempt]);

  const getMentionMenuItems = (): DefaultReactSuggestionItem[] => {
    const users = initiativeDetails.map((item: any) => item.user.firstName);
    return users.map((user: any) => ({
      title: user,
      onItemClick: () =>
        editor.insertInlineContent([
          {
            type: "mention",
            props: { user },
          },
          " ",
        ]),
    }));
  };

  const getArtifactMenuItems = (): DefaultReactSuggestionItem[] => {
    return artifactDetails.map((artifact: any) => ({
      title: `${artifact.externalKey} ${artifact.text}`,
      onItemClick: () => {
        editor.createLink(`${frontEndPoint}${artifact.path}`, artifact.text);
      },
    }));
  };

  return (
    <>
    {loadError ? <div role="alert">{loadError} <button onClick={() => {
      setLoadError('');
      setLoadAttempt(attempt => attempt + 1);
    }}>Retry loading</button></div> : loading ? <div>Loading...</div> : null}
    {/* Mount the collaboration binding before restoring saved content. Mounting
        it afterwards can replace the restored body with the initial Yjs state. */}
    <div style={{ display: loading || loadError ? 'none' : undefined }}>
    <BlockNoteView
      editor={editor}
      theme={theme}
      editable={!loading && !loadError}
      onChange={() => {
        if (!initialized || loading || loadError) return;
        //@ts-ignore
        saveToStorage(editor, editor.document, jsonData);
      }}
    >
      <SuggestionMenuController
        triggerCharacter={"@"}
        getItems={async (query) =>
          //@ts-ignore
          filterSuggestionItems(getMentionMenuItems(), query)
        }
      />
      <GridSuggestionMenuController
        triggerCharacter={":"}
        gridSuggestionMenuComponent={CustomEmojiPicker}
        columns={10}
        minQueryLength={0}
      />
      <SuggestionMenuController
        triggerCharacter={"#"}
        getItems={async (query) =>
          //@ts-ignore
          filterSuggestionItems(getArtifactMenuItems(), query)
        }
      />
    </BlockNoteView>
    </div>
    </>
  );
}
